import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronDown, Search, Check, MapPin } from 'lucide-react';
import { apiGet, apiPut } from '../../lib/api.js';
import { fmtPhone } from '../../lib/format.js';

const MODES = [
  { value: 'smart-local', label: 'Smart Local ID', hint: 'Dynamic Match: area code → state → healthiest number' },
  { value: 'office', label: 'Office Line', hint: 'Your primary business number' },
];

/**
 * Outbound caller-ID selector for the softphone: Smart Local ID, Office Line,
 * or one specific number from the account (searchable by number, name, state).
 * @param {Object} props
 * @param {{callerIdMode?: string, specificCallerId?: string}} props.settings - Agent phone settings.
 * @param {boolean} props.open
 * @param {(open: boolean | ((v: boolean) => boolean)) => void} props.setOpen
 * @param {() => void} [props.refetchSettings]
 * @returns {JSX.Element}
 */
export default function CallerIdPicker({ settings, open, setOpen, refetchSettings }) {
  const [query, setQuery] = useState('');
  const mode = settings.callerIdMode || 'smart-local';

  const { data } = useQuery({
    queryKey: ['phone-caller-ids'],
    queryFn: () => apiGet('/phone/caller-ids'),
    staleTime: 60_000,
    enabled: open || mode === 'specific',
  });
  const numbers = data?.data?.numbers || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return numbers;
    const digits = q.replace(/\D/g, '');
    return numbers.filter((n) => (
      (digits && n.number.replace(/\D/g, '').includes(digits))
      || `${n.friendlyName} ${n.pool} ${n.state} ${n.areaCode}`.toLowerCase().includes(q)
    ));
  }, [numbers, query]);

  async function save(body) {
    setOpen(false);
    setQuery('');
    try {
      await apiPut('/phone/settings', body);
      refetchSettings?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not update caller ID');
    }
  }

  const current = mode === 'specific'
    ? { label: settings.specificCallerId ? fmtPhone(settings.specificCallerId) : 'Specific Number' }
    : { label: MODES.find((m) => m.value === mode)?.label || 'Office Line' };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-civix-panelLight border border-civix-border/60 text-sm hover:border-civix-purple/60 transition"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-white/50 text-xs">Caller ID:</span>
        <div className="flex items-center gap-1.5">
          <span className="text-white text-xs font-medium">{current.label}</span>
          {mode === 'smart-local' && (
            <span className="text-[10px] px-1.5 rounded bg-emerald-500/20 text-emerald-300">Dynamic Match</span>
          )}
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-white/40" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-10 bg-civix-panelLight border border-civix-border rounded-xl shadow-glow-purple z-50 overflow-hidden">
          {MODES.map((m) => (
            <button
              key={m.value}
              onClick={() => save({ callerIdMode: m.value })}
              className={`w-full text-left px-3 py-2 transition hover:bg-civix-border/30 ${mode === m.value ? 'text-civix-purpleSoft' : 'text-white/75'}`}
            >
              <div className="text-sm flex items-center gap-1.5">
                {m.label}
                {mode === m.value && <Check className="w-3.5 h-3.5" />}
              </div>
              <div className="text-[10px] text-white/40">{m.hint}</div>
            </button>
          ))}

          <div className="border-t border-civix-border/60">
            <div className="px-3 pt-2 text-[10px] uppercase tracking-wider text-white/40">Specific number</div>
            <div className="px-3 py-1.5 relative">
              <Search className="w-3.5 h-3.5 text-white/30 absolute left-5 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search number, name or state…"
                className="w-full bg-civix-panel border border-civix-border rounded-lg pl-7 pr-2 py-1.5 text-xs text-white placeholder:text-white/30 focus:outline-hidden focus:border-civix-purple"
              />
            </div>
            <ul role="listbox" className="max-h-48 overflow-y-auto pb-1">
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-xs text-white/40">
                  {numbers.length === 0 ? 'No numbers on your account yet.' : 'No numbers match.'}
                </li>
              )}
              {filtered.map((n) => {
                const selected = mode === 'specific' && settings.specificCallerId === n.number;
                return (
                  <li key={n._id} role="option" aria-selected={selected}>
                    <button
                      onClick={() => save({ callerIdMode: 'specific', specificCallerId: n.number })}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-civix-border/30 ${selected ? 'text-civix-purpleSoft' : 'text-white/75'}`}
                    >
                      <span className="min-w-0">
                        <span className="block text-xs font-mono truncate">{fmtPhone(n.number)}</span>
                        <span className="block text-[10px] text-white/40 truncate">
                          {[n.friendlyName, n.pool].filter(Boolean).join(' · ') || 'Line'}
                          {n.isPrimary ? ' · Primary' : ''}
                        </span>
                      </span>
                      <span className="flex items-center gap-1 text-[10px] text-white/50 shrink-0">
                        {(n.state || n.areaCode) && <MapPin className="w-3 h-3" />}
                        {n.state || n.areaCode}
                        {selected && <Check className="w-3.5 h-3.5 text-civix-purpleSoft" />}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
